// LUMINA_HOST — the encounter/dictation adapter the OneChart v2 file calls
// (window.LUMINA_HOST). It mirrors the file's AP_REPLAY_HOST contract exactly,
// but backs every method with the real dictation pipeline (lib/dictation.js +
// lib/luminaVoice.js), reproducing Dictate.jsx's flow. Real data only.
//
// Contract expected by onechart_v2.html (apEncStart/apSoapGo/apSend):
//   sessionId() -> string|undefined
//   preflight() -> {api_key, npi, patient}
//   start(mode, {language, diarize, consent, onTranscript(text,isFinal,meta)}) -> Promise
//   pause()/resume()/stop() -> Promise
//   analyze(transcript) -> raw analyze-segment payload (key_symptoms/clinical_flags/…)
//   saveTranscript(t) -> {ok:true}
//   generateSoap()/regenerate() -> {soap_note:{subjective,objective,assessment,plan}, diagnoses:[]}
//   sendToClaim() -> {ok:true} (throws on failure)
import { toast } from "sonner";
import {
  ensureApiKey, ensureNpi, ensurePatient,
  sameDaySession, continueSession, createSession,
  saveSessionTranscript, analyzeSegment,
  generateSoap, getSoap, sendToClaim,
  updateSoap, getNote,
  listProviders, soapFromDictation, codesFromDictation, patchNoteTov, patchNoteSoap, reprocessNote,
} from "@/lib/dictation";
import { signNote, unsignNote, generateClaim } from "@/lib/noteClaim";
import { startLuminaVoice } from "@/lib/luminaVoice";

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

export function makeLuminaHost({ D, uuid, token, requestEncounterMeta, onOpenClaim }) {
  const chart = (D && D.chart) || {};
  const clientId = chart.client_id || "";
  const patientId = uuid || chart.chart_uuid || chart.chart_id || "";
  const mrn = chart.mrn || "";
  const name = [chart.first_name, chart.last_name].filter(Boolean).join(" ").trim();
  const dob = chart.dob || "";
  const gender = chart.sex || chart.gender || "";

  const st = { session: null, providers: null, npi: null, provider_name: null, dos: null, consent: null, voice: null, transcript: "", soapId: null, sending: false, sentNoteId: null };

  const patientBody = () => ({ id: patientId, patient_id: patientId, name, dob, gender, mrn, client_id: clientId });

  async function ensureProviders() {
    if (st.providers) return;
    const all = await listProviders().catch(() => []);
    // Only the providers linked to THIS client can file this claim — never show
    // another clinic's NPI in the Start-encounter picker.
    st.providers = clientId ? all.filter((p) => String(p.client_id) === String(clientId)) : all;
    const first = st.providers.find((p) => p.status === "active") || st.providers[0];
    st.npi = first?.npi_number || null;
    st.provider_name = first?.provider_name || null; // A.6 — needed for speaker labels once the role rule is confirmed (DA-74)
  }

  // Local calendar date (YYYY-MM-DD) — NOT UTC. Using toISOString() here would
  // roll the date of service to the previous day for providers whose local date
  // is ahead of UTC, filing an Oct 13 visit as Oct 12.
  const today = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  // Gate the encounter on the dictating provider + date of service.
  //  • One provider on the client → assume it (no NPI prompt).
  //  • More than one → the UI shows a picker.
  //  • DOS always defaults to today and is editable (today or earlier).
  // Falls back to first-active provider + today when no UI hook is wired.
  async function collectEncounterMeta() {
    await ensureProviders();
    if (!requestEncounterMeta) { st.dos = st.dos || today(); return; }
    const meta = await requestEncounterMeta({
      providers: st.providers || [],
      defaultNpi: st.npi || "",
      defaultDos: st.dos || today(),
    });
    if (meta && meta.npi) st.npi = meta.npi;
    st.dos = (meta && meta.date_of_service) || st.dos || today();
    const sel = (st.providers || []).find((p) => p.npi_number === st.npi);
    if (sel) st.provider_name = sel.provider_name || st.provider_name;
  }

  async function ensureSession() {
    if (st.session?.id) return st.session;
    await ensurePatient(patientBody()).catch(() => {});
    try {
      const existing = await sameDaySession({ patient_id: patientId, client_id: clientId });
      const found = existing?.session || existing;
      if (found?.id) { st.session = found; await continueSession(found.id).catch(() => {}); if (found.transcript) st.transcript = found.transcript; return st.session; }
    } catch (e) { /* no same-day session → create */ }
    st.session = await createSession({ patient_id: patientId, client_id: clientId });
    return st.session;
  }

  const mapPolicy = (i) => (i && ({
    payer_name: i.carrier_name || i.plan || i.payer_name || undefined,
    payer_id: i.payer_id || i.carrier_id || undefined,
    member_id: i.member_id || i.policy_number || i.subscriber_id || undefined,
  }));
  const seq = (p) => { const n = parseInt(p?.sequence ?? p?.priority ?? p?.rank ?? p?.payer_rank ?? 99, 10); return isNaN(n) ? 99 : n; };

  const toHostSoap = (result) => {
    const s = soapFromDictation(result) || {};
    const codes = codesFromDictation(result) || { icd: [] };
    return {
      soap_note: {
        subjective: s.subjective || s.raw || "",
        objective: s.objective || "",
        assessment: s.assessment || "",
        plan: s.plan || "",
      },
      diagnoses: codes.icd || [],
    };
  };

  async function runSoap() {
    await ensureSession();
    await ensureProviders();
    await ensureApiKey().catch(() => {});
    if (st.npi) await ensureNpi({ npi_number: st.npi }).catch(() => {});
    const r = await generateSoap({
      session_id: st.session.id, dictation_session_id: st.session.id,
      patient_id: patientId, client_id: clientId,
      npi_number: st.npi || undefined, transcript: st.transcript,
    });
    const id = r.soap_note_id || r.soap_id || r.id;
    st.soapId = id || null;
    if (!id) return toHostSoap(r.result || r); // synchronous result
    for (let i = 0; i < 40; i++) {
      await wait(3000);
      const d = await getSoap(id);
      const status = (d.status || "").toLowerCase();
      if (status === "completed") return toHostSoap(d.result);
      if (status === "failed" || status === "error") throw new Error("The draft could not be generated.");
    }
    throw new Error("The draft timed out.");
  }

  return {
    replay: false,
    sessionId() { return st.session?.id; },

    async preflight() {
      let api_key = false, npi = false, patient = false;
      try { await ensureApiKey(); api_key = true; } catch (e) { api_key = false; }
      await ensureProviders();
      if (st.npi) { try { await ensureNpi({ npi_number: st.npi }); npi = true; } catch (e) {} }
      try { await ensurePatient(patientBody()); patient = true; } catch (e) {}
      return { api_key, npi, patient };
    },

    async start(mode, opts) {
      if (mode === "type") { await ensureSession(); await ensureApiKey().catch(() => {}); return; }
      // Confirm provider + date of service BEFORE creating the session. If the
      // clinician cancels the gate this throws, and apEncStart() reverts to idle.
      await collectEncounterMeta();
      await ensureSession();
      await ensureApiKey().catch(() => {});
      if (st.npi) await ensureNpi({ npi_number: st.npi }).catch(() => {});
      const onT = opts && opts.onTranscript;
      // A.2 — keep the consent the chart confirmed with the session state. No storage field yet (DA-76).
      st.consent = (opts && opts.consent) || null;
      st.voice = await startLuminaVoice({
        token,
        language: (opts && opts.language) || "en",   // A.1 — was fixed to "en"
        diarize: !!(opts && opts.diarize),            // A.3 — Ambient on, Dictate off
        // A.5 — pass speaker meta straight through; null/omitted when no words[] returned
        onFinal: (t, meta) => { st.transcript = (st.transcript ? st.transcript + "\n" : "") + t; onT && onT(t, true, meta || null); },
        onInterim: (t) => { onT && onT(t, false); },
        onError: (msg) => { try { toast.error(msg); } catch (e) {} },
      });
    },
    async pause() { try { st.voice?.pause(); } catch (e) {} },
    async resume() { try { st.voice?.resume(); } catch (e) {} },
    async stop() { try { await st.voice?.stop(); } catch (e) {} st.voice = null; },

    async analyze(transcript) {
      st.transcript = transcript || st.transcript;
      return analyzeSegment(transcript, ""); // raw payload; the file reads key_symptoms/clinical_flags/summary
    },

    async saveTranscript(t) {
      await ensureSession();
      st.transcript = t;
      await saveSessionTranscript(st.session.id, t);
      return { ok: true };
    },

    generateSoap() { return runSoap(); },
    regenerate() { return runSoap(); },

    // Persist physician edits. Once a claim note exists (post-send), the edit goes to the
    // note-to-claim note so it IS the filed note and feeds coding (PATCH …/notes/{id}/soap).
    // Before that, it updates the dictation draft (PUT /dictation/soap-note/{id}).
    async saveSoap(soap) {
      const sn = (soap && soap.soap_note) || {};
      if (st.sentNoteId) {
        const parts = [];
        if (sn.chief_complaint) parts.push("Chief complaint:\n" + sn.chief_complaint);
        if (sn.subjective) parts.push("S:\n" + sn.subjective);
        if (sn.objective) parts.push("O:\n" + sn.objective);
        if (sn.assessment) parts.push("A:\n" + sn.assessment);
        if (sn.plan) parts.push("P:\n" + sn.plan);
        const soap_text = parts.join("\n\n").trim();
        if (soap_text.length < 3) throw new Error("Nothing to save.");
        return patchNoteSoap(st.sentNoteId, { soap_text, formatted_note: soap_text });
      }
      if (!st.soapId) throw new Error("No draft to save.");
      return updateSoap(st.soapId, soap || {});
    },

    // A.3 — the claim note after send, so the panel can follow its stage.
    async getClaimNote(id) {
      return getNote(id);
    },

    async sendToClaim() {
      if (st.sending) throw new Error("A claim is already being created for this session.");
      if (st.sentNoteId) return { ok: true, note_id: st.sentNoteId };
      st.sending = true;
      try {
        await ensureSession();
        await ensureProviders();
        const prov = (st.providers || []).find((p) => p.npi_number === st.npi);
        const primaryTax = (prov?.taxonomies || []).find((t) => t.primary) || (prov?.taxonomies || [])[0];
        const payers = [...((D && D.chart_payers) || [])].sort((a, b) => seq(a) - seq(b));
        const dos = st.dos || today();
        const r = await sendToClaim(st.session.id, {
          npi_number: st.npi || undefined,
          client_id: clientId || undefined,
          date_of_service: dos,
          taxonomy_code: primaryTax?.code || undefined,
          patient_name: name, patient_dob: dob, patient_gender: gender, patient_mrn: mrn,
          patient_insurance: mapPolicy(payers[0]) || undefined,
          patient_insurance_secondary: mapPolicy(payers[1]) || undefined,
        });
        if (!r?.note_id) throw new Error(r?.detail || "The claim was not created.");
        st.sentNoteId = r.note_id; // the bridge is NOT idempotent — never re-send this session
        return { ok: true, note_id: r.note_id };
      } finally {
        st.sending = false;
      }
    },

    patchTov(noteId, value) { return patchNoteTov(noteId, value); },

    // Re-run the coding pipeline on the FILED note (post-send). Returns {status:'queued'};
    // the panel then re-polls the note until the new ICD/CPT land.
    async reprocessCoding() {
      if (!st.sentNoteId) throw new Error("Send the claim first — coding runs on the filed note.");
      return reprocessNote(st.sentNoteId);
    },

    // Signed visits: a claim can only be generated once the visit is signed.
    async signVisit(id) {
      const nid = id || st.sentNoteId;
      if (!nid) throw new Error("Save the encounter first.");
      try { return await signNote(nid); }
      catch (e) { const d = e?.response?.data?.detail; throw new Error(typeof d === "string" ? d : "The visit could not be signed."); }
    },
    async unsignVisit(id) {
      const nid = id || st.sentNoteId;
      if (!nid) throw new Error("Save the encounter first.");
      try { return await unsignNote(nid); }
      catch (e) { const d = e?.response?.data?.detail; throw new Error(typeof d === "string" ? d : "The visit could not be unsigned."); }
    },
    async generateClaim(id) {
      const nid = id || st.sentNoteId;
      if (!nid) throw new Error("Save the encounter first.");
      try { return await generateClaim(nid); }
      catch (e) { const d = e?.response?.data?.detail; throw new Error(typeof d === "string" ? d : "The claim could not be generated."); }
    },

    // Hand the claim to the Create Claims workspace in the same tab (deep-link reopens it).
    openClaim(id) {
      const claimId = id || st.sentNoteId;
      if (onOpenClaim && claimId) { onOpenClaim(claimId, clientId); return { ok: true }; }
      return { ok: false };
    },

    reset() { try { st.voice?.stop(); } catch (e) {} st.voice = null; st.sentNoteId = null; },
  };
}
